import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { X, ChevronRight, Search, Heart, History, Compass } from "lucide-react";

// ─── Tutorial Step Definitions ──────────────────────────────
// Ordered to match the sidebar navigation order
const TUTORIAL_STEPS = [
    {
        id: "discover",
        title: "Découvrir",
        description: "Swipe vers la droite pour sauvegarder un produit, vers la gauche pour passer au suivant. C'est ici que tout commence !",
        icon: Compass,
        targetSelector: '[data-tutorial="discover"]',
        position: "right",
    },
    {
        id: "search",
        title: "Recherche & Pages de Marques",
        description: "Trouve une pièce précise grâce aux filtres, et découvre les pages dédiées de chaque marque !",
        icon: Search,
        targetSelector: '[data-tutorial="search"]',
        position: "right",
    },
    {
        id: "history",
        title: "Historique",
        description: "Un swipe allé un peu trop vite ? Retrouve tous tes anciens produits vus ici.",
        icon: History,
        targetSelector: '[data-tutorial="history"]',
        position: "right",
    },
    {
        id: "collections",
        title: "Collections",
        description: "Retrouve ici tous tes likes ! Organise-les en collections personnalisées (ex : 'Tenues d'été').",
        icon: Heart,
        targetSelector: '[data-tutorial="collections"]',
        position: "right",
    },
];

const STORAGE_KEY = "pepite_tutorial_state";

// Possible states: "pending" | "in_progress" | "completed" | "skipped"
function getTutorialState() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch {}
    return { status: "pending", currentStep: 0 };
}

function setTutorialState(state) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
}

// ─── Bubble Position Calculator ─────────────────────────────
function useBubblePosition(targetSelector, isVisible) {
    const [position, setPosition] = useState(null);

    useEffect(() => {
        if (!isVisible || !targetSelector) {
            setPosition(null);
            return;
        }

        const calculate = () => {
            const el = document.querySelector(targetSelector);
            if (!el) {
                // Target element not found — center the bubble on mobile style
                setPosition({
                    bottom: "24px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    caretSide: "none",
                });
                return;
            }

            const rect = el.getBoundingClientRect();
            const isMobile = window.innerWidth < 768;

            if (isMobile) {
                // On mobile, show at the bottom center (sidebar is hidden)
                setPosition({
                    bottom: "24px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    caretSide: "none",
                });
            } else {
                // On desktop, position to the right of the sidebar item
                setPosition({
                    top: `${rect.top + rect.height / 2}px`,
                    left: `${rect.right + 16}px`,
                    transform: "translateY(-50%)",
                    caretSide: "left",
                });
            }
        };

        // Calculate immediately + on resize
        calculate();
        window.addEventListener("resize", calculate);
        return () => window.removeEventListener("resize", calculate);
    }, [targetSelector, isVisible]);

    return position;
}

// ─── Tutorial Bubble ────────────────────────────────────────
function TutorialBubble({ step, currentIndex, totalSteps, onNext, onClose, position }) {
    const Icon = step.icon;

    return (
        <m.div
            initial={{ opacity: 0, scale: 0.85, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 10 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="tutorial-bubble"
            style={position ? {
                position: "fixed",
                zIndex: 9999,
                ...position,
            } : {}}
        >
            {/* Caret / Arrow */}
            {position?.caretSide === "left" && <div className="tutorial-caret tutorial-caret-left" />}

            {/* Close Button */}
            <button onClick={onClose} className="tutorial-close" aria-label="Fermer le tutoriel">
                <X className="w-4 h-4" />
            </button>

            {/* Content */}
            <div className="tutorial-content">
                <div className="tutorial-icon-wrapper">
                    <Icon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="tutorial-text">
                    <h4 className="tutorial-title">{step.title}</h4>
                    <p className="tutorial-description">{step.description}</p>
                </div>
            </div>

            {/* Footer */}
            <div className="tutorial-footer">
                <span className="tutorial-progress">
                    Étape {currentIndex + 1}/{totalSteps}
                </span>
                <div className="tutorial-dots">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <div
                            key={i}
                            className={`tutorial-dot ${i === currentIndex ? "tutorial-dot-active" : ""}`}
                        />
                    ))}
                </div>
                <button onClick={onNext} className="tutorial-next">
                    {currentIndex === totalSteps - 1 ? "Terminer" : "Suivant"}
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </m.div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  MAIN TUTORIAL OVERLAY
// ═══════════════════════════════════════════════════════════════
export default function TutorialOverlay({ onStart, ready = true }) {
    const location = useLocation();
    const [tutorialState, setLocalState] = useState(getTutorialState);
    const [isVisible, setIsVisible] = useState(false);
    const delayTimerRef = useRef(null);

    const currentStep = TUTORIAL_STEPS[tutorialState.currentStep] || null;

    // ── Auto-launch on first visit with ~3s delay ───────────
    useEffect(() => {
        // If not ready (e.g. waiting for cookie consent), don't start the tutorial
        if (!ready) {
            if (delayTimerRef.current) {
                clearTimeout(delayTimerRef.current);
                delayTimerRef.current = null;
            }
            return;
        }

        if (tutorialState.status === "pending") {
            // Avoid multiple timers if ready toggles
            if (delayTimerRef.current) return;

            delayTimerRef.current = setTimeout(() => {
                const newState = { status: "in_progress", currentStep: 0 };
                setLocalState(newState);
                setTutorialState(newState);
                setIsVisible(true);
                onStart?.();
                delayTimerRef.current = null;
            }, 3000);
        } else if (tutorialState.status === "in_progress") {
            setIsVisible(true);
            onStart?.();
        }

        return () => {
            if (delayTimerRef.current) {
                clearTimeout(delayTimerRef.current);
                delayTimerRef.current = null;
            }
        };
    }, [ready]); // Re-run when ready status changes

    // ── Auto-advance when user navigates to the feature ─────
    useEffect(() => {
        if (!isVisible || tutorialState.status !== "in_progress") return;

        const step = TUTORIAL_STEPS[tutorialState.currentStep];
        if (!step) return;

        // Map step IDs to routes
        const stepRouteMap = {
            discover: "/swipe",
            search: "/search",
            history: "/history",
            collections: "/matches",
        };

        const matchRoute = stepRouteMap[step.id];
        if (matchRoute && location.pathname.startsWith(matchRoute)) {
            // Auto-advance to the next step
            handleNext();
        }
    }, [location.pathname]);

    // ── Handlers ─────────────────────────────────────────────
    const handleNext = useCallback(() => {
        const nextIndex = tutorialState.currentStep + 1;
        if (nextIndex >= TUTORIAL_STEPS.length) {
            // Tutorial complete
            const newState = { status: "completed", currentStep: 0 };
            setLocalState(newState);
            setTutorialState(newState);
            setIsVisible(false);
        } else {
            const newState = { status: "in_progress", currentStep: nextIndex };
            setLocalState(newState);
            setTutorialState(newState);
        }
    }, [tutorialState.currentStep]);

    const handleClose = useCallback(() => {
        const newState = { status: "skipped", currentStep: tutorialState.currentStep };
        setLocalState(newState);
        setTutorialState(newState);
        setIsVisible(false);
    }, [tutorialState.currentStep]);

    // Position
    const bubblePosition = useBubblePosition(currentStep?.targetSelector, isVisible);

    if (!isVisible || !currentStep) return null;

    return (
        <AnimatePresence>
            <TutorialBubble
                key={currentStep.id}
                step={currentStep}
                currentIndex={tutorialState.currentStep}
                totalSteps={TUTORIAL_STEPS.length}
                onNext={handleNext}
                onClose={handleClose}
                position={bubblePosition}
            />
        </AnimatePresence>
    );
}

// ─── Helper to reset tutorial (for "Revoir le guide" button) ─
export function resetTutorial() {
    const state = { status: "pending", currentStep: 0 };
    setTutorialState(state);
    // Force page reload to re-trigger the tutorial
    window.location.reload();
}

// ─── Hook to check if tutorial should show contextual tip ───
export function useContextualTip(featureId) {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const state = getTutorialState();
        // Only show contextual tips if tutorial was completed or skipped
        if (state.status !== "completed" && state.status !== "skipped") return;

        // Check if this specific feature was seen
        const seenKey = `pepite_seen_${featureId}`;
        const seen = localStorage.getItem(seenKey);
        if (!seen) {
            const timer = setTimeout(() => setShow(true), 1500);
            return () => clearTimeout(timer);
        }
    }, [featureId]);

    const dismiss = useCallback(() => {
        const seenKey = `pepite_seen_${featureId}`;
        localStorage.setItem(seenKey, "true");
        setShow(false);
    }, [featureId]);

    return { showTip: show, dismissTip: dismiss };
}
